import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { sorting } from "utils/sorting";
import { Sorting, RiskTemplate } from "types";
import { BasePage } from "components/BasePage/BasePage";
import { SearchInput } from "components/inputs/SearchInput";
import { RiskTemplateRow } from "components/RiskTemplates/Row";
import { SortableTable } from "components/SortableProjectsTable";
import { useRiskTemplates } from "components/RiskTemplates/hooks/useRiskTemplates";
import { CreateRiskTemplateOnDashboard } from "components/RiskTemplates/CreateRiskTemplateOnDashboard";
import { useRiskGroupingTemplates } from "components/Risk/hooks/useRiskGroupingTemplates";
import { RiskGroupingTemplates } from "components/RiskTemplates/RiskGroupingTemplates";
import { CreateRiskGroupingTemplateModal } from "components/RiskTemplates/CreateRiskGroupingTemplateModal";
import { classNames } from "utils/classNames";

enum RiskTemplateColumn {
  readable_name = "readable_name",
  description = "description",
  group = "group",
  chapter = "chapter",
}

interface ColumnSortConfig<T> {
  accessor: (item: T) => string;
}

export const RiskTemplates = () => {
  const { t } = useTranslation();
  const { riskTemplates, fetchInProgress } = useRiskTemplates();
  const { templates: groupingTemplates } = useRiskGroupingTemplates();

  const [activeTab, setActiveTab] = useState<1 | 2>(1);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [filteredData, setFilteredData] = useState<RiskTemplate[]>(
    riskTemplates || []
  );
  const [sortOrder, setSortOrder] = useState<Sorting>(Sorting.asc);
  const [sortColumn, setSortColumn] = useState<string | null>(null);

  const crnToChapterLabels = useMemo(() => {
    const map: Record<string, string[]> = {};
    groupingTemplates.forEach((group) => {
      (group.members || []).forEach((crn) => {
        if (!map[crn]) map[crn] = [];
        map[crn].push(group.label);
      });
    });
    return map;
  }, [groupingTemplates]);

  const columnSortMap: Record<
    RiskTemplateColumn,
    ColumnSortConfig<RiskTemplate>
  > = {
    [RiskTemplateColumn.readable_name]: {
      accessor: (template: RiskTemplate) =>
        template.payload.blueprint_name || template.payload.id,
    },
    [RiskTemplateColumn.description]: {
      accessor: (template: RiskTemplate) => template.payload.description,
    },
    [RiskTemplateColumn.group]: {
      accessor: (template: RiskTemplate) =>
        t(
          "form.risk.options.group." + template.payload.group_id,
          t("riskTemplate.selection.noGroup")
        ),
    },
    [RiskTemplateColumn.chapter]: {
      accessor: (template: RiskTemplate) =>
        (crnToChapterLabels[template.crn] || []).join(", ") ||
        t("riskTemplate.groupings.no_chapter"),
    },
  };

  const getSortedData = (
    sortOrder: string | null,
    sortColumn: string | null
  ) => {
    if (!filteredData || filteredData.length === 0) return riskTemplates || [];

    const sortedData = filteredData.slice().sort((a, b) => {
      return sorting(
        a,
        b,
        sortOrder as Sorting,
        sortColumn as RiskTemplateColumn,
        columnSortMap
      );
    });

    return sortedData;
  };

  useEffect(() => {
    setFilteredData(riskTemplates || []);
  }, [riskTemplates]);

  const tabs = [
    { id: 1 as const, label: t("riskTemplate.groupings.tab_hazard_templates") },
    { id: 2 as const, label: t("riskTemplate.groupings.tab_chapter_templates") },
  ];

  return (
    <BasePage
      loading={fetchInProgress}
      breadcrumbItems={[
        { label: "Dashboard", location: "/" },
        {
          label: t("form.risk_template.list"),
          location: "/risk-templates",
        },
      ]}
    >
      <main className="flex-1 relative z-0 focus:outline-none" tabIndex={0}>
        <div className="sm:flex sm:items-center sm:justify-between">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-medium leading-6 text-gray-900 sm:truncate">
              {t("form.risk_template.list")}
            </h1>
          </div>
          <div className="flex flex-row gap-1">
            {activeTab === 1 && (
              <div className="mt-4">
                <CreateRiskTemplateOnDashboard
                  button={({ onClick }) => (
                    <button
                      onClick={onClick}
                      type="button"
                      className="order-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 sm:order-1 sm:ml-3"
                    >
                      {t("riskTemplate.create.create_new")}
                    </button>
                  )}
                />
              </div>
            )}
            {activeTab === 2 && (
              <div className="mt-4">
                <button
                  onClick={() => setShowCreateModal(true)}
                  type="button"
                  className="order-0 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 sm:order-1 sm:ml-3"
                >
                  {t("riskTemplate.groupings.create_cta")}
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 sm:mt-2 2xl:mt-5">
          <div className="border-b border-gray-200">
            <div className="mx-auto">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {tabs.map((tab) => (
                  <span
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={classNames(
                      activeTab === tab.id
                        ? "border-cyan-600 text-gray-900"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                      "whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm cursor-pointer"
                    )}
                  >
                    {tab.label}
                  </span>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {activeTab === 1 && (
          <>
            {riskTemplates?.length > 0 && (
              <>
                <div className="mt-4">
                  <SearchInput
                    onChange={setFilteredData}
                    placeholder={"riskTemplate.search.placeholder"}
                    values={riskTemplates}
                    fields={[
                      "payload.blueprint_name",
                      "payload.blueprint_description",
                    ]}
                    noMatchTranslation={"riskTemplate.search_no_results_label"}
                  />
                </div>

                <SortableTable
                  data={getSortedData(sortOrder, sortColumn)}
                  columns={[
                    {
                      label: t("riskTemplate.id"),
                      name: RiskTemplateColumn.readable_name,
                      sortable: true,
                      start: true,
                    },
                    {
                      label: t("form.risk.group"),
                      name: RiskTemplateColumn.group,
                      sortable: true,
                    },
                    {
                      label: t("riskTemplate.description"),
                      name: RiskTemplateColumn.description,
                      sortable: true,
                    },
                    {
                      label: t("riskTemplate.groupings.column_label"),
                      name: RiskTemplateColumn.chapter,
                      sortable: true,
                    },
                  ]}
                  sort={getSortedData}
                  renderRow={(template) => (
                    <RiskTemplateRow
                      key={template.crn}
                      {...template}
                      chapterLabels={crnToChapterLabels[template.crn]}
                    />
                  )}
                />
              </>
            )}

            {!riskTemplates?.length && <EmptyDashboard />}
          </>
        )}

        {activeTab === 2 && (
          <>
            <RiskGroupingTemplates
              groupings={groupingTemplates}
              riskTemplates={riskTemplates || []}
            />
            <CreateRiskGroupingTemplateModal
              show={showCreateModal}
              onClose={() => setShowCreateModal(false)}
            />
          </>
        )}
      </main>
    </BasePage>
  );
};

const EmptyDashboard = () => {
  const { t } = useTranslation();

  return (
    <div className="text-center border-2 border-gray-300 border-dashed rounded-lg p-8">
      <svg
        className="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
        />
      </svg>
      <h3 className="mt-2 text-sm font-medium text-gray-900">
        {t("riskTemplate.search.no_templates")}
      </h3>
      <p className="mt-1 text-sm text-gray-500">
        {t("riskTemplate.search.create_template_subline")}
      </p>
      <div className="mt-6">
        <CreateRiskTemplateOnDashboard
          button={({ onClick }) => (
            <button
              onClick={onClick}
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
            >
              {t("riskTemplate.create.cta")}
            </button>
          )}
        />
      </div>
    </div>
  );
};

export default RiskTemplates;
